import type { StudyQuestionsViewport } from "@/features/studies/embed/studyEmbedBridge";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { TFunction } from "i18next";

import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  SortableDragOverlay,
  useSortableDrag,
} from "@/components/SortableList/sortableDrag";
import { StudyLanguageCoachmark } from "@/features/studies/creation/components/goals/StudyLanguageCoachmark";
import { StudyBrandChip, type StudyBrandChipProps } from "./StudyBrandChip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Gauge,
  GripVertical,
  Hash,
  Info,
  Layers,
  Loader2,
  Plus,
  Redo2,
  Save,
  Undo2,
  Users,
  Wrench,
  Zap,
} from "@digital-twins/design-system/icons";

import { cn } from "@/lib/utils";
import { QuestionnairePathPreview } from "./QuestionnairePathPreview";
import {
  compositeScoreLabel,
  DraggedQuestionPreview,
  focusFieldByTestId,
  QuestionnaireQuestionCard,
} from "./QuestionnaireQuestionCard";
import type { CoverageEntry } from "./TopicCoverageChip";
import { ConceptAssetsPanel } from "./ConceptAssetsPanel";
import { StudyObjectivesRow } from "./StudyObjectivesRow";
import type {
  QuestionExposureMove,
  QuestionExposureSettings,
} from "./questionSettingsTypes";
import {
  getRoutingTargetCandidates,
  getRoutingWhenCandidates,
  getChoicesFromCandidates,
  getVisibilityRuleCandidates,
  QuestionnaireQuestionRecord,
  QuestionnaireWorkspaceState,
  type StudyObjectiveUpdate,
} from "../hooks/useQuestionnaireWorkspace";
import { getValidationTarget } from "../draft/createLocalDraft";
import { QUESTIONNAIRE_RESEARCH_QUESTION_CEILING } from "../questionnaireLimits";
import {
  formatIssueBannerLine,
  formatIssueCopy,
  formatUnscopedBannerLine,
} from "../utils/questionnaireIssueCopy";
import { shownAuthoringIssues } from "../utils/indexValidationIssues";
import {
  deleteDependents,
  type DeleteDependent,
} from "../utils/deleteDependents";
import { isLiveEffect } from "../utils/dryRunIssues";
import {
  researchDragFeedbackKey,
  resolveResearchListDrag,
  type ResearchListDragRow,
} from "../utils/researchListDrag";
import {
  getCompositesForQuestion,
  getQuestionDeletionGuard,
  questionTypeChangeDropsScoredRef,
} from "../scoring/compositeDependencies";
import type {
  ConceptAsset,
  WinnerCompositeDefinition,
  QuestionnaireExposureMarkIndexes,
  QuestionnaireExposureMarkName,
  QuestionnaireValidationIssue,
  StudyGoals,
} from "@digital-twins/shared-types";
import {
  carriedObjectiveFallbackStamp,
  exposureZoneAt,
  getVisibilityRuleLeaves,
  questionRefLabel,
  readTestedAssetSharedContext,
  resolveExposureMarkIndexes,
  twinExposureOf,
} from "@digital-twins/shared-types";

import {
  askedOffScreen,
  exposureLinesAreOneStrip,
  exposureStripWords,
  exposureViewOf,
  exposureZonesShowOf,
  showsSharedViewLine,
  type ExposureStripWords,
  type ExposureView,
  type ExposureZoneShows,
} from "@/features/studies/questionnaire/assets/exposureZone";
import { ExposureStripAssets } from "./ExposureStripAssets";

import { TwinsMarkLoader } from "@/components/quick-study-results/TwinsMarkLoader";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

/**
 * What the builder is busy with: the chat's one busy fact, by its cause. The
 * chat derives it (`busyCause`); the panel locks while there is one, and a
 * locked control says why with that cause's line.
 */
export type BuilderBusyCause =
  | "turn"
  | "save"
  | "asset_write"
  | "opening"
  | "loading"
  /** The draft takes no edits (a launched, archived or copying study). */
  | "read_only";

/** Why a control waits: the builder's one busy fact. */
export type BuilderLockCause = BuilderBusyCause;

/**
 * The one line per cause for whatever waits on it: the panel's locked
 * controls, the empty canvas and the composer all read it, so a card's
 * answer says "Working on it…" everywhere, never that the questionnaire is
 * being updated. It is in the interface language (`questionnaire.busy.*`),
 * like the rest of the builder's chrome, the composer's other placeholders
 * included.
 */
export function lockReasonFor(cause: BuilderLockCause): string {
  return i18n.t(`questionnaire.busy.${cause}`, { ns: "app" });
}

/**
 * What the empty canvas says for a turn's server stage. Routing has no line.
 * Planning to assembling name the build stage, and authoring includes the
 * question count when the server sent one, so a long healthy plan is not one
 * silent "Preparing" line. `persisting` comes before any commit, a card's
 * answer or the content language included, so its line only says "Saving…".
 */
export function questionnaireBuildProgressLine(
  turnStage: string,
  total?: number | null,
): string {
  if (turnStage === "planning") {
    return i18n.t("questionnaire.progress.planning", { ns: "app" });
  }
  if (turnStage === "authoring") {
    return typeof total === "number" && total > 0
      ? i18n.t("questionnaire.progress.authoring", { ns: "app", count: total })
      : i18n.t("questionnaire.progress.authoringPending", { ns: "app" });
  }
  if (turnStage === "assembling") {
    return i18n.t("questionnaire.progress.assembling", { ns: "app" });
  }
  if (turnStage === "persisting") {
    return i18n.t("questionnaire.progress.persisting", { ns: "app" });
  }
  return i18n.t("questionnaire.preparing", { ns: "app" });
}

/** Shown under a planning or authoring line. Those stages can run for minutes. */
export function questionnaireBuildProgressHint(
  turnStage: string | null | undefined,
): string | null {
  if (turnStage === "planning" || turnStage === "authoring") {
    return i18n.t("questionnaire.progress.hint", { ns: "app" });
  }
  return null;
}

/**
 * What the empty canvas says while the builder is busy: the cause's own line.
 * A turn has a line only once the server reports a stage past `routing`.
 * A turn that only answers in the chat, such as the objective or a card, has
 * no line until it saves, and then only "Saving…": the canvas says where the
 * questionnaire appears, and the chat shows that it is working.
 * Once Stop is in, the line says so instead of still preparing.
 */
export function emptyCanvasBusyLine(
  cause: BuilderBusyCause,
  turnStage: string | null | undefined,
  options?: { stopRequested?: boolean; total?: number | null },
): string | null {
  if (cause !== "turn") return lockReasonFor(cause);
  if (options?.stopRequested) {
    return i18n.t("questionnaire.stopping", { ns: "app" });
  }
  return turnStage && turnStage !== "routing"
    ? questionnaireBuildProgressLine(turnStage, options?.total)
    : null;
}

/**
 * Why the authoring surface goes read-only mid-turn: the response the chat is
 * computing was derived from the draft as it stood when the turn was sent, and
 * it replaces the whole workspace draft when it lands. An edit made inside that
 * window is reverted with no explanation, so we take the edit off the table
 * instead of letting it be silently undone.
 */
interface QuestionnaireBuilderPanelProps {
  /** Inline Quick Study reuses the editor without its duplicate cover. */
  compact?: boolean;
  onQuestionsViewport?: (viewport: StudyQuestionsViewport | null) => void;
  /** Per-question coverage assessment results keyed by questionId. */
  coverageByQuestionId?: Map<string, CoverageEntry>;
  /** Server-persisted draft id; the Preview path reads the saved draft. */
  draftId?: string | null;
  /** Blocking issues keyed by question id against the persisted draft. */
  issuesByQuestionId?: Map<string, Array<QuestionnaireValidationIssue>>;
  /** Blocking issues that do not resolve to a question (assets, count, composite). */
  unscopedIssues?: Array<QuestionnaireValidationIssue>;
  /** The unscoped issues about one tested asset, keyed by its id, for its card. */
  issuesByAssetId?: Map<string, Array<QuestionnaireValidationIssue>>;
  /** The saved verdict's advice keyed by question id: guidance, never a block. */
  advisoriesByQuestionId?: Map<string, Array<QuestionnaireValidationIssue>>;
  /** Advice that names no question: an item's (its `assetId`) or the study's. */
  unscopedAdvisories?: Array<QuestionnaireValidationIssue>;
  hasUserSentMessage?: boolean;
  highlightValues?: Array<string>;
  /**
   * True from the explore confirm card's Build manually / Paste click until
   * the request that opens the workspace settles. Hides the empty overlay so
   * it cannot sit on half-ready chrome.
   */
  isOpeningWorkspace?: boolean;
  /** True while the session's objective PATCH is in flight. Pre-draft only. */
  isSavingStudyGoals?: boolean;
  /**
   * The chat's one busy fact, by its cause (`busyCause`): a turn, a Save, a
   * chat-side asset write, the workspace opening, or the draft loading. The panel's editing surface locks while there is one,
   * and its locked controls say why (`lockReasonFor`).
   */
  busy?: BuilderBusyCause | null;
  /**
   * The server's stage for the turn in flight (the session's `turnStage`).
   * Read only by the empty canvas (`emptyCanvasBusyLine`).
   */
  turnStage?: string | null;
  /**
   * Question count for an authoring stage (`total` on the stage event).
   * Absent on planning, assembling and persisting.
   */
  turnStageTotal?: number | null;
  /** Stop was asked for this turn and the terminal event has not arrived. */
  stopRequested?: boolean;
  language?: string;
  onLanguageChange?: (lang: string) => void;
  /** The resolved "Study brand" (plan Decision 5), or null for none. */
  studyBrand?: StudyBrandChipProps["brand"];
  /** Saves the user's "Study brand" edit; absent hides the chip. */
  onStudyBrandSave?: StudyBrandChipProps["onSave"];
  /**
   * The Save button. The builder's own Save also answers a waiting collect
   * card with the assets it saves; without it, the button saves the edits.
   */
  onSave?: () => Promise<unknown>;
  /**
   * Sends the objective as a `set_objective` turn, after unsaved builder
   * edits are saved. Resolves false when nothing was sent (the user kept
   * editing, or the save failed).
   */
  onSaveStudyGoals?: (next: StudyObjectiveUpdate) => Promise<boolean | void>;
  /** Reports an unsaved local objective editor to the chat's leave guard. */
  onObjectiveEditorChange?: (
    editing: boolean,
    savePendingEdit?: () => Promise<boolean>,
  ) => void;
  /**
   * The objective off `chatState`, which is where it lives before any
   * questionnaire does. Read ONLY by the empty state; the built panel reads
   * `draft.studyGoals` off the workspace, which outranks this copy.
   */
  studyGoals?: StudyGoals | null;
  workspace: QuestionnaireWorkspaceState;
}

const DRAG_DROP_ANIMATION = {
  duration: 180,
  easing: "cubic-bezier(0.18,0.67,0.6,1.22)",
};

/**
 * WIP authoring guidance driven purely by the research-question count. Launch
 * stays gated to 8-15 by the backend compiler; a below-floor draft shows no
 * banner (the review gate communicates the requirement when it matters).
 */
/**
 * A card's issues while edits are unsaved: the refused Save's (the server's
 * verdict on this exact draft) first, then the live logic checks', each code
 * and path once. Undefined when there are none.
 */
function unsavedIssues(
  refused: ReadonlyArray<QuestionnaireValidationIssue> | undefined,
  live: ReadonlyArray<QuestionnaireValidationIssue> | undefined,
): Array<QuestionnaireValidationIssue> | undefined {
  const seen = new Set<string>();
  const issues = [...(refused ?? []), ...(live ?? [])].filter((issue) => {
    const key = `${issue.code}|${issue.path}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return issues.length > 0 ? issues : undefined;
}

function getResearchQuestionCountWarning(
  count: number,
  t: TFunction<"app">,
): string | null {
  if (count <= 0) {
    return null;
  }
  if (count > QUESTIONNAIRE_RESEARCH_QUESTION_CEILING) {
    return t("questionnaire.researchCeiling", {
      count: count - QUESTIONNAIRE_RESEARCH_QUESTION_CEILING,
      max: QUESTIONNAIRE_RESEARCH_QUESTION_CEILING,
    });
  }
  return null;
}

// Light: shimmer sweeps with a subtle white highlight over the gray bars.
// Dark: same but dimmer since the bars are already dark.
function ShimmerBar(props: { animate?: boolean; className?: string; style?: React.CSSProperties }) {
  const { animate = true } = props;
  return (
    <div className={`relative overflow-hidden ${props.className ?? ""}`} style={props.style}>
      {animate ? (
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent dark:via-white/8" />
      ) : null}
    </div>
  );
}

function SkeletonQuestionCard(props: { animate?: boolean; index: number }) {
  const { animate } = props;
  return (
    <div
      className="rounded-[12px] border border-border/60 bg-white px-6 py-5 dark:bg-card"
      data-testid={`generation-preview-skeleton-${props.index}`}
    >
      <div className="flex items-start gap-3.5">
        <ShimmerBar animate={animate} className="h-[22px] w-8 rounded-[5px] bg-black/[0.07] dark:bg-white/10" />
        <div className="min-w-0 flex-1 space-y-3">
          <ShimmerBar
            animate={animate}
            className="h-5 rounded bg-black/[0.09] dark:bg-white/12"
            style={{ width: `${65 - props.index * 8}%` }}
          />
          <div className="flex gap-2">
            <ShimmerBar animate={animate} className="h-[22px] w-16 rounded-[5px] bg-black/[0.07] dark:bg-white/10" />
            <ShimmerBar animate={animate} className="h-[22px] w-14 rounded-[5px] bg-black/[0.07] dark:bg-white/10" />
          </div>
          <div className="space-y-2 pt-1">
            <ShimmerBar animate={animate} className="h-9 w-full rounded-[8px] bg-black/[0.05] dark:bg-white/8" />
            <ShimmerBar animate={animate} className="h-9 w-full rounded-[8px] bg-black/[0.05] dark:bg-white/8" />
            <ShimmerBar animate={animate} className="h-9 w-[75%] rounded-[8px] bg-black/[0.05] dark:bg-white/8" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The panel before a questionnaire exists: a skeleton under the turn's busy
 * line, or the idle copy. The objective is not drawn here: before the first
 * build it is the user's opening words as typed, and the chat states it
 * ("Objective defined."). The objectives row shows once a draft exists, with
 * PLAN's objectives (B-UI-MISC U1).
 */
function QuestionnaireEmptyDraftState(props: {
  compact?: boolean;
  animate?: boolean;
  isOpeningWorkspace?: boolean;
  busy?: BuilderBusyCause | null;
  turnStage?: string | null;
  turnStageTotal?: number | null;
  stopRequested?: boolean;
  studyGoals?: StudyGoals | null;
}) {
  const {
    compact = false,
    animate,
    busy,
    turnStage,
    turnStageTotal,
    stopRequested,
    isOpeningWorkspace,
    studyGoals,
  } = props;
  const { t } = useTranslation("app");
  const isSending = Boolean(busy);
  const busyLine = busy
    ? emptyCanvasBusyLine(busy, turnStage, {
        stopRequested,
        total: turnStageTotal,
      })
    : null;
  const progressHint =
    busyLine && !stopRequested ? questionnaireBuildProgressHint(turnStage) : null;
  const shimmerActive = animate || isSending;
  const skeletonSlots = 4;

  const hasObjective = Boolean(studyGoals?.decision?.trim());

  // The overlay blurs what is not real yet: the whole panel.
  const overlay = isOpeningWorkspace ? null : (
    <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[2px] dark:bg-background/70">
      {busyLine ? (
        <div
          key="sending"
          className="flex flex-col items-center gap-2.5 text-center animate-fade-in"
        >
          <TwinsMarkLoader
            className="h-5 w-5 text-foreground/45"
            dataTestId="panel-preparing-mark"
          />
          <p className="text-[15px] font-medium text-foreground/60 animate-pulse">
            {busyLine}
          </p>
          {progressHint ? (
            <p
              className="max-w-[280px] text-[13px] leading-[1.5] text-muted-foreground"
              data-testid="panel-progress-hint"
            >
              {progressHint}
            </p>
          ) : null}
        </div>
      ) : hasObjective || isSending ? (
        // The invitation to start with the objective waits on the user. While
        // a turn runs that prepares nothing yet (the objective being
        // recorded), the canvas says only where the questionnaire appears.
        <div
          key="idle-objective"
          className="w-full max-w-[360px] px-6 text-center animate-fade-in"
          data-testid="questionnaire-builds-here"
        >
          <p className="text-[16px] font-medium text-foreground">
            {t("questionnaire.buildsHere")}
          </p>
          <p className="mt-1.5 text-[13px] leading-[1.6] text-muted-foreground">
            {t("questionnaire.buildsHereHint")}
          </p>
        </div>
      ) : (
        <div key="idle" className="text-center animate-fade-in">
          <p className="text-[15px] font-medium text-foreground/80">
            {t("questionnaire.startObjective")}
          </p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {t("questionnaire.startObjectiveHint")}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl"
      data-testid="questionnaire-builder-panel"
      data-keyboard-focus-ring=""
    >
      {/* Placeholder header bar */}
      <div className={cn("flex shrink-0 flex-wrap items-center border-b border-border/80", compact ? "gap-2 bg-background px-3 py-2" : "gap-4 bg-muted/50 px-8 py-3 dark:bg-card")}>
        <div className="flex items-center gap-2">
          <div className="h-[15px] w-[15px] rounded bg-black/[0.08] dark:bg-white/10" />
          <div className="h-3 w-20 rounded bg-black/[0.08] dark:bg-white/10" />
        </div>
      </div>

      <div
        data-testid="questionnaire-builder-panel-scroll"
        className={cn("min-h-0 flex-1 overflow-hidden", compact ? "bg-background" : "bg-[#F8F8F8] dark:bg-card")}
      >
        <div className={compact ? "px-3 pb-4 pt-2" : "px-8 pb-[120px] pt-2"}>
          {skeletonSlots > 0 ? (
            <div data-testid="generation-preview-skeleton-remainder">
              <header className="mb-7">
                <ShimmerBar animate={shimmerActive} className="h-10 w-[70%] rounded bg-black/[0.08] dark:bg-white/10" />
                <ShimmerBar animate={shimmerActive} className="mt-3 h-4 w-[90%] rounded bg-black/[0.06] dark:bg-white/8" />
                <ShimmerBar animate={shimmerActive} className="mt-1.5 h-4 w-[60%] rounded bg-black/[0.06] dark:bg-white/8" />
              </header>
              <div className="flex flex-col gap-3.5">
                {Array.from({ length: skeletonSlots }, (_, i) => (
                  <SkeletonQuestionCard
                    key={`remaining-${i}`}
                    index={i}
                    animate={shimmerActive}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Overlay covers the full panel including the header */}
      {overlay}
    </div>
  );
}

/**
 * Sortable ids for the two declared exposure marks (Phase 4b-d). Rows in the
 * same `@dnd-kit` list as the question cards, distinguished from a real
 * question id by this fixed prefix, never a stableKey collision.
 */
const EXPOSURE_MARK_ROW_ID: Record<QuestionnaireExposureMarkName, string> = {
  reveal: "__exposure_mark_reveal__",
  sharedView: "__exposure_mark_shared_view__",
};

/**
 * One row of the builder's ordered list: a research question, or the strip of
 * one of the two marks. `carriesSharedView` is a reveal strip that is the
 * shared view's too: one exposure change, dragged as the reveal (the
 * workspace moves a comparative pair together).
 */
type BuilderRow =
  | { kind: "question"; record: QuestionnaireQuestionRecord; index: number }
  | { kind: "divider"; mark: QuestionnaireExposureMarkName; carriesSharedView: boolean };

function builderRowId(row: BuilderRow): string {
  return row.kind === "divider" ? EXPOSURE_MARK_ROW_ID[row.mark] : row.record.id;
}

function researchDragRow(row: BuilderRow): ResearchListDragRow {
  if (row.kind === "divider") {
    return {
      id: builderRowId(row),
      questionId: null,
      stableKey: null,
      mark: row.mark,
    };
  }
  return {
    id: row.record.id,
    questionId: row.record.id,
    stableKey: row.record.question.stableKey,
    mark: null,
  };
}

/**
 * Interleave the two exposure marks into the ordered research-question list,
 * from §2.3: the reveal sits right after `revealIndex` (before index 0 when
 * `revealIndex === -1`, meaning nothing is pre-exposure); the shared view sits
 * right before `sharedViewIndex` (after the last question when it equals the
 * question count, meaning lanes run to the end). Both can land at the very
 * top, the very bottom, or adjacent to each other with nothing between them.
 * Adjacent lines that move together are one exposure change: one strip.
 */
function buildExposureMarkRows(
  researchQuestions: Array<QuestionnaireQuestionRecord>,
  indexes: QuestionnaireExposureMarkIndexes,
  view: ExposureView,
): Array<BuilderRow> {
  const rows: Array<BuilderRow> = [];
  const sharedViewShows = showsSharedViewLine(
    view,
    indexes,
    researchQuestions.length,
  );
  const oneStrip = exposureLinesAreOneStrip(
    view,
    indexes,
    researchQuestions.length,
  );
  for (let index = 0; index <= researchQuestions.length; index += 1) {
    if (index === indexes.revealIndex + 1) {
      rows.push({ kind: "divider", mark: "reveal", carriesSharedView: oneStrip });
    }
    if (sharedViewShows && !oneStrip && index === indexes.sharedViewIndex) {
      rows.push({ kind: "divider", mark: "sharedView", carriesSharedView: false });
    }
    if (index < researchQuestions.length) {
      rows.push({ kind: "question", record: researchQuestions[index], index });
    }
  }
  return rows;
}

// `as const` on purpose: `t()` is typed against the en bundle.
const EXPOSURE_MARK_LABEL_KEY = {
  reveal: "questionnaire.exposureMarkRevealLabelPlain",
  sharedView: "questionnaire.exposureMarkSharedViewLabel",
} as const satisfies Record<QuestionnaireExposureMarkName, string>;

/** Codes each mark's inline refusal can show, gathered regardless of which prop carried them. */
const EXPOSURE_ISSUE_CODES_BY_MARK: Record<
  QuestionnaireExposureMarkName,
  ReadonlySet<string>
> = {
  reveal: new Set([
    "exposure_marks_cross",
    "exposure_boundary_splits_pair",
    "lift_pre_after_reveal",
    "exposure_boundary_below_recipe_floor",
    "pre_exposure_question_binds_stimulus",
    "pre_exposure_asset_listed",
  ]),
  sharedView: new Set(["exposure_marks_cross"]),
};

interface ExposureMarkDividerProps {
  /** The zone below the strip, read from the runner's delivery; null for an empty zone. */
  shows: ExposureZoneShows | null;
  /** The assets `shows` names, in catalog order. */
  assets: Array<ConceptAsset>;
  /** What the strip says about its zone (`exposureStripWords`). */
  words: ExposureStripWords;
  /** The study's content language, which the assets' prices are worded in. */
  contentLanguage?: string;
  carriesSharedView: boolean;
  disabled?: boolean;
  disabledReason?: string;
  issues: Array<QuestionnaireValidationIssue>;
  mark: QuestionnaireExposureMarkName;
}

/**
 * The strip at an exposure mark (Phase 4b-d; decisions 10-12, refined by 11;
 * B6 F1, 2026-10-14): the mark, what the zone below shows twins, and the
 * assets it shows. Draggable like the cards it sits between, vertical-axis
 * only. A drag never snaps back on its own: the new position always commits,
 * and a refusal from the backend renders here as the reason, not as a silent
 * correction.
 */
function ExposureMarkDivider(props: ExposureMarkDividerProps) {
  const {
    assets,
    carriesSharedView,
    contentLanguage,
    disabled,
    disabledReason,
    issues,
    mark,
    shows,
    words,
  } = props;
  const { t } = useTranslation("app");
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: EXPOSURE_MARK_ROW_ID[mark], disabled: Boolean(disabled) });

  const refusal = issues.length > 0 ? formatIssueCopy(issues[0]).text : null;
  const testIdSuffix = mark === "reveal" ? "reveal" : "shared-view";

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex flex-col gap-2 rounded-[10px] border border-dashed px-3.5 py-2 text-[12px] leading-[1.4] transition-colors",
        refusal
          ? "border-destructive/50 bg-destructive/5 text-destructive"
          : "border-border/70 bg-muted/40 text-muted-foreground",
        isDragging && "pointer-events-none opacity-40",
      )}
      data-testid={`questionnaire-exposure-mark-${testIdSuffix}`}
      data-exposure-strip-marks={carriesSharedView ? "reveal sharedView" : mark}
      data-exposure-strip-kind={shows?.kind}
      data-exposure-strip-assets={assets.map((asset) => asset.id).join(" ")}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          aria-label={t("questionnaire.exposureMarkDragAria")}
          className="touch-none cursor-grab rounded-md p-0.5 opacity-60 transition-opacity hover:opacity-100 active:cursor-grabbing"
          data-testid={`questionnaire-exposure-mark-drag-${testIdSuffix}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        {refusal ? (
          <span className="font-medium">{refusal}</span>
        ) : (
          <span data-testid={`questionnaire-exposure-mark-label-${testIdSuffix}`}>
            <span className="font-semibold text-foreground">
              {words.label ?? t(EXPOSURE_MARK_LABEL_KEY[mark])}
            </span>
            {words.detail ? ` · ${words.detail}` : null}
          </span>
        )}
      </div>
      {assets.length > 0 ? (
        <div className="pl-6">
          <ExposureStripAssets assets={assets} contentLanguage={contentLanguage} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Static twin of `ExposureMarkDivider` for the `DragOverlay`. No `useSortable`:
 * that hook may register one live node per sortable id, and the row already
 * dragging owns `EXPOSURE_MARK_ROW_ID[mark]` for the duration of the drag
 * (the same reason question cards use `DraggedQuestionPreview` here instead of
 * a second `QuestionnaireQuestionCard`).
 */
function ExposureMarkDividerPreview(props: { mark: QuestionnaireExposureMarkName }) {
  const { t } = useTranslation("app");
  return (
    <div
      className="pointer-events-none flex items-center gap-2.5 rounded-[10px] border border-accent-highlight bg-background px-3.5 py-2 text-[12px] font-medium leading-[1.4] text-muted-foreground shadow-[0_12px_28px_rgba(7,18,15,0.18)] dark:bg-card"
      data-testid="questionnaire-exposure-mark-drag-preview"
    >
      <GripVertical className="h-3.5 w-3.5 opacity-60" />
      <span>{t(EXPOSURE_MARK_LABEL_KEY[props.mark])}</span>
    </div>
  );
}

/**
 * The four exposure-line moves a card's Settings menu can offer next to this
 * question (contracts §4). The question itself never moves; each move relocates
 * one of the two declared marks to sit at this question's stableKey or a
 * neighbour's. `previousStableKey` / `nextStableKey` are `null` at the ends of
 * the list, which is exactly what an empty-zone move argues for.
 */
export function buildExposureMoves(
  index: number,
  indexes: QuestionnaireExposureMarkIndexes,
  stableKey: string,
  previousStableKey: string | null,
  nextStableKey: string | null,
): Array<QuestionExposureMove> {
  const { revealIndex, sharedViewIndex } = indexes;
  return [
    {
      key: "reveal-after",
      mark: "reveal",
      stableKey,
      isNoop: revealIndex === index,
      isBlocked: index >= sharedViewIndex,
    },
    {
      key: "reveal-before",
      mark: "reveal",
      stableKey: previousStableKey,
      isNoop: revealIndex === index - 1,
      isBlocked: index - 1 >= sharedViewIndex,
    },
    {
      key: "shared-at",
      mark: "sharedView",
      stableKey,
      isNoop: sharedViewIndex === index,
      isBlocked: index <= revealIndex,
    },
    {
      key: "shared-after",
      mark: "sharedView",
      stableKey: nextStableKey,
      isNoop: sharedViewIndex === index + 1,
      isBlocked: index + 1 <= revealIndex,
    },
  ];
}

export function QuestionnaireBuilderPanel(
  props: QuestionnaireBuilderPanelProps,
) {
  const {
    compact = false,
    onQuestionsViewport,
    coverageByQuestionId,
    draftId,
    issuesByQuestionId,
    unscopedIssues,
    issuesByAssetId,
    advisoriesByQuestionId,
    unscopedAdvisories,
    hasUserSentMessage,
    isOpeningWorkspace,
    isSavingStudyGoals,
    busy,
    turnStage,
    turnStageTotal,
    stopRequested,
    language,
    onLanguageChange,
    studyBrand,
    onStudyBrandSave,
    onSave = props.workspace.saveChanges,
    onSaveStudyGoals,
    onObjectiveEditorChange,
    studyGoals,
    workspace,
  } = props;
  const { t } = useTranslation("app");
  // Read-only on the chat's one busy fact; a locked control says its cause.
  const isLocked = Boolean(busy);
  const lockReason = busy ? lockReasonFor(busy) : undefined;
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [activeResearchId, setActiveResearchId] = useState<string | null>(null);
  // A queued delete/retype awaiting the user's confirmation: of a core scored
  // landing-page dimension (the server T0/T1 prune+renormalize the composite),
  // or a delete that clears other questions' logic (`dependents`, L3).
  // Warn-and-confirm only, never a hard block. `null` when nothing is pending.
  const [pendingScoredEdit, setPendingScoredEdit] = useState<{
    action: "delete" | "retype";
    questionId: string;
    nextType?: string;
    composites: Array<WinnerCompositeDefinition>;
    isLastComponent: boolean;
    dependents?: Array<DeleteDependent>;
  } | null>(null);

  const { undo, redo, canUndo, canRedo } = workspace;

  // navigator.platform is deprecated and missing on SSR/non-browser hosts.
  // Use userAgent (also imperfect, but widely supported and never undefined).
  const isMacKeyboard = useMemo(() => {
    if (typeof navigator === "undefined") {
      return false;
    }
    const ua = navigator.userAgent || "";
    return /Mac|iPhone|iPad|iPod/.test(ua);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      const isMeta = event.metaKey || event.ctrlKey;
      if (!isMeta) {
        return;
      }
      // The buttons are disabled while a turn is in flight; the shortcuts have
      // to be closed separately or they stay the one live path into the draft.
      if (isLocked) {
        return;
      }
      const key = event.key.toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo =
        (key === "z" && event.shiftKey) || key === "y";
      if (isUndo) {
        event.preventDefault();
        if (canUndo) {
          undo();
        }
      } else if (isRedo) {
        event.preventDefault();
        if (canRedo) {
          redo();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [canRedo, canUndo, isLocked, redo, undo]);

  // Add question moves the caret to the new question's title, as Add option
  // and Add row do (RND-2928), so Space or Enter doesn't add another one. The
  // title is looked up in this panel: at narrow width the builder mounts two.
  const addQuestionFocusFromCountRef = useRef<number | null>(null);
  const panelRootRef = useRef<HTMLDivElement>(null);
  const draftQuestions = workspace.currentDraft?.researchQuestions;
  useLayoutEffect(() => {
    const countBefore = addQuestionFocusFromCountRef.current;
    if (countBefore === null || !draftQuestions) {
      return;
    }
    addQuestionFocusFromCountRef.current = null;
    const added = draftQuestions[draftQuestions.length - 1];
    if (draftQuestions.length > countBefore && added) {
      focusFieldByTestId(
        panelRootRef.current,
        `questionnaire-question-title-${added.id}`,
      );
    }
  }, [draftQuestions]);
  const addQuestionAndFocus = () => {
    addQuestionFocusFromCountRef.current = draftQuestions?.length ?? 0;
    workspace.addQuestion();
  };


  // Distinct landing-page scores (composites) the server-issued plan defines.
  // Zero for non-LP studies, so the header "· N scores" stat never shows there.
  // The study-verdict slot plans the podium and scores nothing, so it is not
  // a score.
  const scoredDimensionCount = useMemo(() => {
    const composites = workspace.currentDraft?.metricPlan?.compositeMetrics;
    return Array.isArray(composites)
      ? composites.filter((composite) => composite.kind === "winner_composite")
          .length
      : 0;
  }, [workspace.currentDraft?.metricPlan?.compositeMetrics]);

  // The "N skipped" chip that used to live here is gone. It counted
  // recipe modules no topic covered and, for each, printed
  // `GENERIC_EXCLUSION_REASON` -- one fixed English sentence, repeated. It keyed
  // on `skipped.moduleId` and then rendered only `skipped.reason`, so the one
  // field naming WHICH module was dropped went into the React key and never
  // reached the screen. A marketer saw "12 omitidos" over twelve identical
  // sentences saying, in effect, that we did not ask about something because we
  // did not ask about it -- in English, inside a localised UI. Omitting modules
  // is usually the correct outcome anyway, so there was nothing to act on.
  // `planCoverage.excludedModules` is still stamped on the draft for debugging.
  const { researchQuestions } = useMemo(() => {
    const research: Array<QuestionnaireQuestionRecord> = [];
    workspace.questions.forEach((record) => {
      research.push(record);
    });
    return { researchQuestions: research };
  }, [workspace.questions]);

  // Phase 4b-d: resolved ONCE per render, against this exact list, and passed
  // down (to the divider rows and to each card's `exposureZone`) so nothing
  // downstream re-detects it. `workspace.currentDraft?.exposureBoundary` is
  // read directly rather than through the `draft` local (defined after the
  // empty-draft guard below) because every hook here must run unconditionally.
  const exposureMarkIndexes = useMemo(
    () =>
      resolveExposureMarkIndexes(
        researchQuestions.map((record) => ({
          stableKey: record.question.stableKey,
        })),
        workspace.currentDraft?.exposureBoundary ?? null,
      ),
    [researchQuestions, workspace.currentDraft?.exposureBoundary],
  );

  // Boundaries determine the displayed phase. Which lines show follows the
  // study's StudySpec (how many stimulus versions, the design). A study with
  // no stimulus has no exposure to describe. Shared validation owns any
  // disagreement with question scope or design.
  const exposureView = useMemo(
    () => exposureViewOf(workspace.currentDraft),
    [workspace.currentDraft],
  );

  // What twins are shown, per question and per zone: the runner's delivery
  // (shared-types `twinExposureOf`), read once per render. The strips and the
  // cards' "asked with the assets off screen" line say only this.
  const twinExposure = useMemo(
    () => (exposureView ? twinExposureOf(workspace.currentDraft) : null),
    [exposureView, workspace.currentDraft],
  );
  const exposureZones = useMemo(
    () =>
      twinExposure
        ? exposureZonesShowOf(
            researchQuestions.map((record) => record.question),
            exposureMarkIndexes,
            twinExposure,
          )
        : null,
    [twinExposure, researchQuestions, exposureMarkIndexes],
  );

  // What each post-reveal zone's strip shows and says, once per render: the
  // assets on screen there (in catalog order) and their words.
  const zoneStrips = useMemo(() => {
    const stripOf = (zone: "lane" | "shared_view") => {
      const shows = exposureZones?.[zone] ?? null;
      const assets = shows
        ? workspace.conceptAssets.filter((asset) => shows.assetIds.has(asset.id))
        : [];
      return { shows, assets, words: exposureStripWords(shows, assets, t) };
    };
    return { lane: stripOf("lane"), shared_view: stripOf("shared_view") };
  }, [exposureZones, workspace.conceptAssets, t]);

  // The question's number (`questionRefLabel`), keyed by stableKey, built once per render off the
  // ordered research-question list. Cards use it for the settings menu's
  // routing/visibility subjects and the carry-forward pills below.
  const questionLabelByStableKey = useMemo(() => {
    const map = new Map<string, string>();
    researchQuestions.forEach((record) => {
      map.set(record.question.stableKey, questionRefLabel(record.index));
    });
    return map;
  }, [researchQuestions]);

  // Reverse of each question's `response.choicesFrom.sourceStableKey`: for a
  // source question, the Q labels of every question reusing its options
  // (D9). Read the same way the card does (`response.choicesFrom`), never a
  // new accessor.
  const feedsQuestionsBySourceStableKey = useMemo(() => {
    const map = new Map<string, Array<string>>();
    researchQuestions.forEach((record) => {
      const response = record.question.response as
        | { choicesFrom?: { sourceStableKey?: string } }
        | undefined;
      const sourceStableKey = response?.choicesFrom?.sourceStableKey;
      if (!sourceStableKey) {
        return;
      }
      const label = questionLabelByStableKey.get(record.question.stableKey);
      if (!label) {
        return;
      }
      const existing = map.get(sourceStableKey);
      if (existing) {
        existing.push(label);
      } else {
        map.set(sourceStableKey, [label]);
      }
    });
    return map;
  }, [researchQuestions, questionLabelByStableKey]);

  const builderRows = useMemo(
    () =>
      exposureView
        ? buildExposureMarkRows(
            researchQuestions,
            exposureMarkIndexes,
            exposureView,
          )
        : researchQuestions.map((record, index) => ({
            kind: "question" as const,
            record,
            index,
          })),
    [researchQuestions, exposureMarkIndexes, exposureView],
  );
  const researchDrag = useSortableDrag(builderRows.map(builderRowId));

  // Same `workspace.isDirty` gate the per-question issues use below: the props
  // already reflect the last SAVED draft, but a hand-built workspace stub in a
  // test can set them independently of `isDirty`, so re-check here too.
  const exposureIssuesByMark = useMemo(() => {
    if (workspace.isDirty) {
      return { reveal: [], sharedView: [] };
    }
    const all: Array<QuestionnaireValidationIssue> = [
      ...(unscopedIssues ?? []),
      ...Array.from(issuesByQuestionId?.values() ?? [], (issues) => issues).flat(),
    ];
    return {
      reveal: all.filter((issue) =>
        EXPOSURE_ISSUE_CODES_BY_MARK.reveal.has(issue.code),
      ),
      sharedView: all.filter((issue) =>
        EXPOSURE_ISSUE_CODES_BY_MARK.sharedView.has(issue.code),
      ),
    };
  }, [workspace.isDirty, unscopedIssues, issuesByQuestionId]);

  const questionsViewportRef = useRef<HTMLDivElement>(null);
  const questionsScrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!onQuestionsViewport) return;
    const report = () => {
      const list = questionsViewportRef.current?.getBoundingClientRect();
      const scroll = questionsScrollRef.current?.getBoundingClientRect();
      if (!list || !scroll || researchQuestions.length === 0) { onQuestionsViewport(null); return; }
      const viewport = {
        top: Math.max(list.top, scroll.top, 0), left: Math.max(list.left, scroll.left, 0),
        right: Math.min(list.right, scroll.right, window.innerWidth), bottom: Math.min(list.bottom, scroll.bottom, window.innerHeight),
        viewportWidth: window.innerWidth, viewportHeight: window.innerHeight,
      };
      onQuestionsViewport(viewport.right > viewport.left && viewport.bottom > viewport.top ? viewport : null);
    };
    report();
    window.addEventListener("scroll", report, true);
    window.addEventListener("resize", report);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(report) : null;
    if (questionsViewportRef.current) observer?.observe(questionsViewportRef.current);
    if (questionsScrollRef.current) observer?.observe(questionsScrollRef.current);
    return () => { window.removeEventListener("scroll", report, true); window.removeEventListener("resize", report); observer?.disconnect(); onQuestionsViewport?.(null); };
  }, [onQuestionsViewport, researchQuestions.length]);

  // The saved verdict about one tested item, by its typed `assetId`: what
  // blocks (`issuesByAssetId`) and its advice.
  const verdictByAssetId = useMemo(() => {
    const byAsset = new Map<string, Array<QuestionnaireValidationIssue>>(issuesByAssetId);
    for (const issue of unscopedAdvisories ?? []) {
      if (issue.assetId) {
        byAsset.set(issue.assetId, [...(byAsset.get(issue.assetId) ?? []), issue]);
      }
    }
    return byAsset;
  }, [issuesByAssetId, unscopedAdvisories]);

  const researchQuestionWarning = useMemo(
    () =>
      getResearchQuestionCountWarning(
        workspace.currentDraft?.researchQuestions.length ?? 0,
        t,
      ),
    [workspace.currentDraft?.researchQuestions.length, t],
  );

  // The objective row's Save sends the objective alone, as a `set_objective`
  // turn (plan S4.2, L23); the workspace holds no copy of it, so it is never
  // an unsaved builder edit. The row commits the edit first and asks for the
  // save a render later; until the turn settles the row shows the edit it
  // just made. A false result (nothing sent) reaches the row, which reopens
  // its editor on the text.
  const pendingObjectiveRef = useRef<StudyObjectiveUpdate | null>(null);
  const [pendingObjective, setPendingObjective] =
    useState<StudyObjectiveUpdate | null>(null);
  const updateObjective = useCallback((update: StudyObjectiveUpdate) => {
    pendingObjectiveRef.current = update;
    setPendingObjective(update);
  }, []);
  const saveObjective = useCallback(async () => {
    const update = pendingObjectiveRef.current;
    pendingObjectiveRef.current = null;
    try {
      return update && onSaveStudyGoals
        ? await onSaveStudyGoals(update)
        : undefined;
    } finally {
      setPendingObjective(null);
    }
  }, [onSaveStudyGoals]);
  const savedGoals = workspace.currentDraft?.studyGoals;
  const shownGoals: StudyGoals | undefined = pendingObjective
    ? {
        ...(savedGoals ?? { learn: [] }),
        decision: pendingObjective.decision,
        learn: pendingObjective.learn ?? savedGoals?.learn ?? [],
        source: pendingObjective.source ?? "edited",
        // A coded-fallback stamp describes a sentence; it is shown only while
        // the sentence is unchanged, as the pre-draft row does.
        ...carriedObjectiveFallbackStamp(savedGoals, pendingObjective.decision),
      }
    : savedGoals;

  if (!workspace.currentDraft) {
    return (
      <QuestionnaireEmptyDraftState
        compact={compact}
        animate={hasUserSentMessage}
        isOpeningWorkspace={isOpeningWorkspace}
        busy={busy}
        turnStage={turnStage}
        turnStageTotal={turnStageTotal}
        stopRequested={stopRequested}
        studyGoals={studyGoals}
      />
    );
  }

  const atQuestionCeiling =
    workspace.currentDraft.researchQuestions.length >=
    QUESTIONNAIRE_RESEARCH_QUESTION_CEILING;

  const handleResearchDragStart = (event: DragStartEvent) => {
    if (isLocked) {
      return;
    }
    setActiveResearchId(String(event.active.id));
  };

  const handleResearchDragEnd = (event: DragEndEvent) => {
    setActiveResearchId(null);
    const { active, over } = event;
    const decision = resolveResearchListDrag({
      activeId: String(active.id),
      overId: over ? String(over.id) : null,
      isLocked,
      rows: builderRows.map(researchDragRow),
    });
    if (decision.kind === "reorder") {
      workspace.reorderResearchQuestions(decision.fromId, decision.toId);
      return;
    }
    if (decision.kind === "move-mark") {
      workspace.setExposureMark(decision.mark, decision.stableKey);
      return;
    }
    if (decision.kind === "unavailable") {
      const message =
        decision.reason === "locked" && lockReason
          ? lockReason
          : t(researchDragFeedbackKey(decision.reason));
      if (decision.reason === "unchanged") {
        toast.message(message);
        return;
      }
      toast.warning(message);
    }
  };

  const activeResearchRecord =
    activeResearchId !== null
      ? researchQuestions.find((record) => record.id === activeResearchId) ||
        null
      : null;
  const activeExposureMark = (
    Object.keys(EXPOSURE_MARK_ROW_ID) as Array<QuestionnaireExposureMarkName>
  ).find((mark) => EXPOSURE_MARK_ROW_ID[mark] === activeResearchId) ?? null;
  const draft = workspace.currentDraft;
  const persistedDraft = workspace.savedDraft ?? draft;
  // The one filter Review's tooltip reads too: an empty shell's count and its
  // missing brief are not something to fix; asset and shape errors still show.
  const authoringUnscoped = shownAuthoringIssues(unscopedIssues, draft);
  const scopedIssueCount = issuesByQuestionId?.size ?? 0;
  const hasUnscopedIssues = authoringUnscoped.length > 0;
  const showValidationBanner =
    !workspace.isDirty && (scopedIssueCount > 0 || hasUnscopedIssues);
  const firstAffectedQuestionId =
    scopedIssueCount > 0 && issuesByQuestionId
      ? (issuesByQuestionId.keys().next().value ?? null)
      : null;
  const unscopedStep =
    hasUnscopedIssues && persistedDraft
      ? getValidationTarget(persistedDraft, authoringUnscoped[0]?.path ?? "")
          .step
      : null;
  // While edits are unsaved, the banner says why the Save is held back (the
  // dry run's refusal, in the Save's own words) and leads to its question.
  const heldBack = workspace.isDirty ? (workspace.saveHeldBack ?? null) : null;
  const heldBackQuestionId = heldBack
    ? (workspace.saveRefusal?.byQuestionId.keys().next().value ?? null)
    : null;
  const validationBannerText = heldBack
    ? [heldBack.title, heldBack.description].filter(Boolean).join(" ")
    : showValidationBanner
    ? scopedIssueCount > 0 && issuesByQuestionId
      ? formatIssueBannerLine(
          Array.from(issuesByQuestionId.values(), (issues) => issues[0]),
        )
      : unscopedStep
        ? formatUnscopedBannerLine(unscopedStep, authoringUnscoped[0])
        : null
    : null;

  function handleValidationBannerClick() {
    const questionId = heldBack ? heldBackQuestionId : firstAffectedQuestionId;
    if (questionId) {
      const card = document.querySelector(
        `[data-testid="questionnaire-question-card-${questionId}"]`,
      );
      card?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!heldBack && unscopedStep === "assets") {
      document
        .querySelector('[data-testid="questionnaire-concept-assets-panel"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  // Route a delete through the structural guard. A core scored dimension, or
  // a question other questions' logic uses, opens the confirm dialog
  // (warn-and-confirm), which lists what the delete clears; everything else
  // (free, or the hook's hard blocks) goes straight to the workspace, which
  // keeps its own safety-net guards and clears the same dependents.
  const requestDelete = (questionId: string) => {
    const guard = getQuestionDeletionGuard(draft, questionId);
    const dependents =
      guard.kind === "blocked_min_count" ? [] : deleteDependents(draft, questionId);
    if (guard.kind === "warn_scored" || dependents.length > 0) {
      setPendingScoredEdit({
        action: "delete",
        questionId,
        composites: guard.composites,
        isLastComponent: guard.isLastComponent,
        dependents,
      });
      return;
    }
    workspace.deleteQuestion(questionId);
  };

  // Route a type change through the guard. Retyping a scored dimension to a type
  // that drops its `metric_*` ref (today only open_end) silently guts the score,
  // so confirm first; all other retypes apply immediately.
  const requestChangeType = (questionId: string, nextType: string) => {
    if (questionTypeChangeDropsScoredRef(draft, questionId, nextType)) {
      const composites = getCompositesForQuestion(draft, questionId);
      const isLastComponent = composites.some((composite) => {
        const componentIds = composite.components.map(
          (component) => component.metricId,
        );
        const question = draft?.researchQuestions.find(
          (candidate) => candidate.id === questionId,
        );
        const refs = new Set(question?.metricRefs ?? []);
        return componentIds.every((id) => refs.has(id));
      });
      setPendingScoredEdit({
        action: "retype",
        questionId,
        nextType,
        composites,
        isLastComponent,
      });
      return;
    }
    workspace.changeQuestionType(questionId, nextType);
  };

  const confirmScoredEdit = () => {
    if (!pendingScoredEdit) {
      return;
    }
    const { action, questionId, nextType } = pendingScoredEdit;
    setPendingScoredEdit(null);
    if (action === "delete") {
      workspace.deleteQuestion(questionId);
    } else if (nextType) {
      workspace.changeQuestionType(questionId, nextType);
    }
  };

  // A strip describes the zone below it: the reveal's the lane zone, the
  // shared view's (or a reveal strip carrying it) the shared view.
  const stripShowsFor = (row: Extract<BuilderRow, { kind: "divider" }>) =>
    zoneStrips[
      row.mark === "sharedView" || row.carriesSharedView ? "shared_view" : "lane"
    ];

  const renderCard = (
    record: QuestionnaireQuestionRecord,
    index: number,
  ) => {
    const coverageResult = coverageByQuestionId?.get(record.id);
    // Unsaved edits: what the last refused Save, or the server's dry run of
    // this Save, named on this question, then the issues the dry run's result
    // would still have (one verdict, T1b). Saved: the readiness verdict's
    // issues, indexed against the saved draft.
    const liveIssues = workspace.logicIssues?.get(record.id);
    const validationIssues = workspace.isDirty
      ? unsavedIssues(
          workspace.saveRefusal?.byQuestionId.get(record.id),
          liveIssues?.filter((issue) => !isLiveEffect(issue)),
        )
      : issuesByQuestionId?.get(record.id);
    // What the unsaved edits would also change on this question (an answer
    // that leaves with a removed item, a condition that loses a value): said
    // in the card's notice, as the toast after the Save says it.
    const liveEffects = liveIssues?.filter(isLiveEffect);
    // Computed once above, from this exact render's marks and the runner's
    // delivery; the card does no detection of its own.
    const exposureZone = exposureZoneAt(index, exposureMarkIndexes);
    const isAskedOffScreen =
      twinExposure && exposureZones
        ? askedOffScreen(
            record.question.stableKey,
            exposureZone,
            exposureZones,
            twinExposure,
          )
        : false;

    // Settings menu Exposure sub-menu (D11): only offered when this study
    // shows stimulus and the builder is not read-only, and only for the lines
    // the canvas shows. Neighbour stableKeys come straight off the ordered
    // research-question list; `index` lines up with `exposureMarkIndexes`
    // regardless of whether the divider rows are interleaved (builderRows
    // never renumbers `record.index`).
    const exposureSettings: QuestionExposureSettings | null =
      exposureView && !isLocked
        ? {
            zoneLabel:
              exposureZone === "pre"
                ? null
                : (zoneStrips[exposureZone].words.label ??
                  zoneStrips[exposureZone].words.detail),
            moves: buildExposureMoves(
              index,
              exposureMarkIndexes,
              record.question.stableKey,
              researchQuestions[index - 1]?.question.stableKey ?? null,
              researchQuestions[index + 1]?.question.stableKey ?? null,
            ).filter(
              (move) =>
                move.mark === "reveal" ||
                showsSharedViewLine(
                  exposureView,
                  exposureMarkIndexes,
                  researchQuestions.length,
                ),
            ),
            onMove: workspace.setExposureMark,
          }
        : null;

    return (
      <QuestionnaireQuestionCard
        key={record.id}
        conceptAssets={workspace.conceptAssets}
        mentionTables={workspace.mentionTables}
        coverageResult={coverageResult}
        askedOffScreen={isAskedOffScreen}
        exposureZone={exposureZone}
        exposureSettings={exposureSettings}
        questionLabelByStableKey={questionLabelByStableKey}
        feedsQuestions={feedsQuestionsBySourceStableKey.get(
          record.question.stableKey,
        )}
        disabled={isLocked}
        disabledReason={lockReason}
        validationIssues={validationIssues}
        advisoryIssues={
          workspace.isDirty
            ? liveEffects
            : advisoriesByQuestionId?.get(record.id)
        }
        dependencyCandidates={getVisibilityRuleCandidates(
          draft.researchQuestions,
          record.id,
          Array.isArray(draft.testedAssets?.assets)
            ? draft.testedAssets.assets
            : [],
          draft,
          new Set(
            getVisibilityRuleLeaves(record.question.visibilityRule).map(
              (leaf) => leaf.dependsOnStableKey,
            ),
          ),
        )}
        routingWhenCandidates={getRoutingWhenCandidates(
          draft.researchQuestions,
          record.id,
          Array.isArray(draft.testedAssets?.assets)
            ? draft.testedAssets.assets
            : [],
          draft,
          new Set(
            getVisibilityRuleLeaves(record.question.routingRule?.when).map(
              (leaf) => leaf.dependsOnStableKey,
            ),
          ),
        )}
        routingTargetCandidates={getRoutingTargetCandidates(
          draft.researchQuestions,
          record.id,
          draft,
        )}
        choicesFromCandidates={getChoicesFromCandidates(
          draft.researchQuestions,
          record.id,
        )}
        feedsComposites={getCompositesForQuestion(draft, record.id)}
        index={index}
        isSelected={workspace.selectedQuestionId === record.id}
        language={language}
        contentLanguage={draft?.contentLanguage}
        onChangeType={(nextType) => requestChangeType(record.id, nextType)}
        onDelete={() => requestDelete(record.id)}
        onDuplicate={() => workspace.duplicateQuestion(record.id)}
        onSelect={() => workspace.updateQuestionOrderSelection(record.id)}
        onUpdate={(updater) => workspace.updateQuestion(record.id, updater)}
        question={record}
      />
    );
  };

  return (
    <div
      ref={panelRootRef}
      className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl"
      data-testid="questionnaire-builder-panel"
      data-keyboard-focus-ring=""
    >
      <div className={cn("flex shrink-0 flex-wrap items-center border-b border-border/80", compact ? "gap-2 bg-background px-3 py-2" : "gap-4 bg-muted/50 px-8 py-3 dark:bg-card")}>
        <div className="flex items-center gap-2">
          <Layers className="h-[15px] w-[15px] text-foreground dark:text-accent-highlight" />
          <span className="text-[12px] font-medium leading-none text-muted-foreground">
            {t("questionnaire.title")}
          </span>
        </div>
        <span className="h-3.5 w-px bg-border" />
        <div className="flex flex-wrap items-center gap-4 text-[12px] leading-none text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Hash className="h-3 w-3" />
            {t("questionnaire.questions", { count: workspace.questionCount })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Zap className="h-3 w-3" />
            {t("questionnaire.minutes", { count: workspace.estimatedMinutes })}
          </span>
          {scoredDimensionCount > 0 ? (
            <span
              className="inline-flex items-center gap-1.5"
              data-testid="questionnaire-scored-count"
              title={t("questionnaire.scoresTitle")}
            >
              <Gauge className="h-3 w-3" />
              {t("questionnaire.scores", { count: scoredDimensionCount })}
            </span>
          ) : null}
          {workspace.selectedPopulation?.name ? (
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              {workspace.selectedPopulation.name}
            </span>
          ) : null}
        </div>
        <span className="flex-1" />
        {draftId && (
          <QuestionnairePathPreview
            draftId={draftId}
            language={persistedDraft?.contentLanguage}
            dirty={workspace.isDirty}
            locked={isLocked}
            lockedReason={lockReason}
            exposure={twinExposure}
          />
        )}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-1">
            {workspace.isDirty ? (
              <>
                <span
                  className="mr-1 text-[12px] text-muted-foreground"
                  data-testid="questionnaire-unsaved"
                >
                  {t("questionnaire.unsavedChanges")}
                </span>
                <Button
                  type="button"
                  variant="cta"
                  size="sm"
                  className="h-7 rounded-[6px] px-2 text-[12px]"
                  onClick={() => void onSave()}
                  disabled={workspace.isSaving || isLocked || !!workspace.saveHeldBack}
                  title={
                    isLocked
                      ? lockReason
                      : workspace.saveHeldBack
                        ? [workspace.saveHeldBack.title, workspace.saveHeldBack.description]
                            .filter(Boolean)
                            .join(" ")
                        : undefined
                  }
                  data-testid="questionnaire-save"
                >
                  {workspace.isSaving ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  {t("questionnaire.save")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 rounded-[6px] px-2 text-[12px] text-muted-foreground hover:text-destructive dark:hover:text-red-400"
                  onClick={() => setShowDiscardConfirm(true)}
                  disabled={workspace.isSaving || isLocked}
                  data-testid="questionnaire-discard"
                >
                  {t("questionnaire.discard")}
                </Button>
                <span className="mx-1 h-3.5 w-px bg-border" />
              </>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-[6px]"
                  onClick={() => undo()}
                  disabled={!canUndo || isLocked}
                  title={isLocked ? lockReason : undefined}
                  aria-label={t("questionnaire.undoAria")}
                  data-testid="questionnaire-undo"
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <span className="text-[11px]">
                  {t("questionnaire.undo")}
                  <span className="ml-1.5 text-muted-foreground">
                    {isMacKeyboard ? "\u2318Z" : "Ctrl+Z"}
                  </span>
                </span>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-[6px]"
                  onClick={() => redo()}
                  disabled={!canRedo || isLocked}
                  title={isLocked ? lockReason : undefined}
                  aria-label={t("questionnaire.redoAria")}
                  data-testid="questionnaire-redo"
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <span className="text-[11px]">
                  {t("questionnaire.redo")}
                  <span className="ml-1.5 text-muted-foreground">
                    {isMacKeyboard ? "\u21E7\u2318Z" : "Ctrl+Shift+Z"}
                  </span>
                </span>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <div ref={questionsScrollRef} className={cn("min-h-0 flex-1 overflow-y-auto", compact ? "bg-background px-3 pb-4 pt-2" : "bg-muted/50 px-8 pb-[120px] pt-2 dark:bg-card")}>
        <div className="w-full">
          {!compact ? <header className="mb-7">
            {onLanguageChange || onStudyBrandSave ? (
              <div className="flex flex-wrap justify-start gap-2 py-2">
                {onLanguageChange ? (
                  <StudyLanguageCoachmark
                    language={language ?? "en"}
                    onLanguageChange={onLanguageChange}
                    // A turn in flight commits in the language it was sent
                    // with, so the language is locked with the rest.
                    disabled={isLocked}
                    popoverAlign="start"
                  />
                ) : null}
                {onStudyBrandSave ? (
                  <StudyBrandChip
                    brand={studyBrand ?? null}
                    onSave={onStudyBrandSave}
                    disabled={isLocked}
                  />
                ) : null}
              </div>
            ) : null}
            <Textarea
              value={workspace.title}
              onChange={(event) =>
                workspace.updateSurveyMeta({ title: event.target.value })
              }
              rows={1}
              disabled={isLocked}
              title={isLocked ? lockReason : undefined}
              placeholder={t("questionnaire.untitled")}
              className="min-h-[52px] resize-none overflow-hidden border-0 bg-transparent px-0 pb-[6px] pt-0 font-display text-[38px] md:text-[38px] font-normal leading-[1.15] tracking-[-0.01em] text-foreground shadow-none focus-visible:ring-0"
              style={{ fieldSizing: "content" } as React.CSSProperties}
              data-testid="questionnaire-workspace-title"
            />
            <Textarea
              value={workspace.surveyDescription}
              onChange={(event) =>
                workspace.updateSurveyMeta({ description: event.target.value })
              }
              rows={1}
              disabled={isLocked}
              title={isLocked ? lockReason : undefined}
              placeholder={t("questionnaire.descriptionPlaceholder")}
              className="mt-2.5 min-h-0 resize-none border-0 bg-transparent px-0 py-0 text-[14px] leading-[1.6] tracking-normal text-muted-foreground shadow-none focus-visible:ring-0"
              style={{ fieldSizing: "content" } as React.CSSProperties}
              data-testid="questionnaire-workspace-description"
            />
          </header> : null}

          {/* The objective the questions are written against, so it reads
              before them. Renders nothing when the draft carries none. */}
          <StudyObjectivesRow
            defaultExpanded={compact ? false : undefined}
            goals={shownGoals}
            onUpdateObjective={updateObjective}
            onSaveObjective={saveObjective}
            onEditingChange={onObjectiveEditorChange}
            saving={workspace.isSaving || pendingObjective !== null}
            locked={isLocked}
            lockedReason={lockReason}
          />

          {researchQuestionWarning ? (
            <div
              className="mb-4 flex items-start gap-2.5 rounded-[10px] border border-amber-300/60 bg-amber-50 px-4 py-3 text-[13px] leading-[1.5] text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200"
              role="status"
              data-testid="questionnaire-wip-warning"
            >
              <Info className="mt-px h-4 w-4 flex-shrink-0" />
              <span>{researchQuestionWarning}</span>
            </div>
          ) : null}

          {validationBannerText ? (
            <button
              type="button"
              className="mb-4 flex w-full items-start gap-2.5 rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-3 text-left text-[13px] leading-[1.5] text-destructive"
              data-testid="questionnaire-validation-banner"
              onClick={handleValidationBannerClick}
            >
              <Wrench className="mt-px h-4 w-4 flex-shrink-0" />
              <span>{validationBannerText}</span>
            </button>
          ) : null}

          {/* Always mounted (RND-2680): with no assets AND no declared type
              the panel shows the "Add an asset" type picker instead of
              disappearing entirely. */}
          <ConceptAssetsPanel
            concepts={workspace.conceptAssets}
            declaredAssetTypes={
              workspace.currentDraft?.testedAssets?.assetTypes ?? []
            }
            assetsRequired={
              workspace.currentDraft?.testedAssets?.required === true
            }
            onAddConcept={workspace.addConceptAsset}
            onRemoveConcept={workspace.removeConceptAsset}
            onUpdateConcept={workspace.updateConceptAsset}
            onChangeDeclaredAssetTypes={workspace.setDeclaredAssetTypes}
            onUploadConceptImage={workspace.attachConceptImage}
            onAttachConceptCapture={workspace.attachConceptCapture}
            onAttachConceptUrl={workspace.attachConceptUrl}
            analyzingConceptIds={workspace.analyzingConceptIds}
            disabled={isLocked}
            disabledReason={lockReason}
            contentLanguage={workspace.currentDraft?.contentLanguage}
            sharedContext={readTestedAssetSharedContext(
              workspace.currentDraft?.testedAssets,
            )}
            // The server's word on each item: the saved draft's verdict,
            // blocking and advice, as the question cards read it (QA4 FE3,
            // N3); while edits are unsaved, the last refused Save's (N7).
            issuesByAssetId={
              workspace.isDirty
                ? workspace.saveRefusal?.byAssetId
                : verdictByAssetId
            }
          />


          <div className="flex flex-col gap-3.5">
            {researchQuestions.length > 0 ? (
              <DndContext
                {...researchDrag}
                onDragStart={handleResearchDragStart}
                onDragCancel={() => setActiveResearchId(null)}
                onDragEnd={handleResearchDragEnd}
              >
                <SortableContext
                  items={builderRows.map(builderRowId)}
                  strategy={verticalListSortingStrategy}
                >
                  <div ref={questionsViewportRef} className="flex flex-col gap-3.5">
                    {builderRows.map((row) =>
                      row.kind === "divider" ? (
                        <ExposureMarkDivider
                          key={builderRowId(row)}
                          mark={row.mark}
                          carriesSharedView={row.carriesSharedView}
                          contentLanguage={workspace.currentDraft?.contentLanguage}
                          disabled={isLocked}
                          disabledReason={lockReason}
                          issues={
                            row.carriesSharedView
                              ? [
                                  ...exposureIssuesByMark.reveal,
                                  ...exposureIssuesByMark.sharedView,
                                ]
                              : exposureIssuesByMark[row.mark]
                          }
                          {...stripShowsFor(row)}
                        />
                      ) : (
                        renderCard(row.record, row.index)
                      ),
                    )}
                  </div>
                </SortableContext>
                <SortableDragOverlay dropAnimation={DRAG_DROP_ANIMATION}>
                  {activeExposureMark && exposureView ? (
                    <ExposureMarkDividerPreview mark={activeExposureMark} />
                  ) : activeResearchRecord ? (
                    <DraggedQuestionPreview record={activeResearchRecord} />
                  ) : null}
                </SortableDragOverlay>
              </DndContext>
            ) : null}
          </div>

          <button
            type="button"
            className="mt-[18px] flex w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-border bg-transparent p-4 text-[13.5px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:bg-background hover:text-foreground"
            onClick={addQuestionAndFocus}
            disabled={isLocked || atQuestionCeiling}
            title={
              isLocked
                ? lockReason
                : atQuestionCeiling
                  ? t("questionnaire.addQuestionAtCeiling", {
                      max: QUESTIONNAIRE_RESEARCH_QUESTION_CEILING,
                    })
                  : undefined
            }
            data-testid="questionnaire-add-question"
          >
            <Plus className="h-4 w-4" />
            {t("questionnaire.addQuestion")}
          </button>
        </div>
      </div>

      <Dialog
        open={pendingScoredEdit !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingScoredEdit(null);
          }
        }}
      >
        <DialogContent
          className="max-w-sm gap-0 p-0 sm:p-0 overflow-hidden"
          data-testid="questionnaire-scored-edit-confirm"
        >
          <div className="px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="text-lg font-normal">
                {pendingScoredEdit?.action === "retype"
                  ? t("questionnaire.scoredRetypeTitle")
                  : pendingScoredEdit?.composites.length
                    ? t("questionnaire.scoredDeleteTitle")
                    : t("questionnaire.logicDeleteTitle")}
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm">
                {pendingScoredEdit?.dependents?.length
                  ? t("questionnaire.logicDeleteBody", {
                      items: pendingScoredEdit.dependents
                        .map((dependent) =>
                          t(`questionnaire.logicDependent.${dependent.link}`, {
                            question: dependent.label,
                          }),
                        )
                        .join(", "),
                    })
                  : null}
                {pendingScoredEdit?.dependents?.length &&
                pendingScoredEdit.composites.length
                  ? " "
                  : null}
                {(() => {
                  if (!pendingScoredEdit?.composites.length) return null;
                  const scoreNames =
                    pendingScoredEdit?.composites
                      .map(compositeScoreLabel)
                      .join(", ") ?? "";
                  const isLast = Boolean(pendingScoredEdit?.isLastComponent);
                  const isRetype = pendingScoredEdit?.action === "retype";
                  return t(
                    isLast
                      ? isRetype
                        ? "questionnaire.scoredRetypeLast"
                        : "questionnaire.scoredDeleteLast"
                      : isRetype
                        ? "questionnaire.scoredRetypeOther"
                        : "questionnaire.scoredDeleteOther",
                    { names: scoreNames },
                  );
                })()}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex items-center justify-center gap-3 rounded-b-[inherit] bg-muted/60 px-6 py-4 dark:bg-card">
            <Button
              type="button"
              variant="secondary"
              className="h-10 rounded-lg px-4 text-base font-medium"
              onClick={() => setPendingScoredEdit(null)}
              data-testid="questionnaire-scored-edit-confirm-cancel"
            >
              {t("questionnaire.scoredKeep")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-10 rounded-lg px-4 text-base font-medium"
              onClick={confirmScoredEdit}
              disabled={isLocked}
              title={isLocked ? lockReason : undefined}
              data-testid="questionnaire-scored-edit-confirm-action"
            >
              {pendingScoredEdit?.action === "retype"
                ? t("questionnaire.scoredConvertAnyway")
                : t("questionnaire.scoredDeleteAnyway")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent className="max-w-sm gap-0 p-0 sm:p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="text-lg font-normal">
                {t("questionnaire.discardTitle")}
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm">
                {t("questionnaire.discardBody")}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex items-center justify-center gap-3 rounded-b-[inherit] bg-muted/60 px-6 py-4 dark:bg-card">
            <Button
              type="button"
              variant="secondary"
              className="h-10 rounded-lg px-4 text-base font-medium"
              onClick={() => setShowDiscardConfirm(false)}
            >
              {t("questionnaire.keepEditing")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-10 rounded-lg px-4 text-base font-medium"
              disabled={isLocked}
              title={isLocked ? lockReason : undefined}
              onClick={() => {
                setShowDiscardConfirm(false);
                workspace.discardChanges();
              }}
            >
              {t("questionnaire.discardChanges")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
